import { ArrowDropDown, ArrowUpward, CallSplit, Visibility } from "@mui/icons-material"
import {
    Box,
    Button,
    ButtonGroup,
    Chip,
    ClickAwayListener,
    Grow,
    Icon,
    IconButton,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Tooltip,
} from "@mui/material"
import { useMemo, useRef, useState } from "react"
import { NexTable } from "../../components/Table/NexTable"
import { FlightlineEvent } from "../../models/FlightlineEvent"
import {
    FlightlineEventReview,
    GeometryQCStatus,
    ReviewDrawerTab,
    ReviewNodeInfo,
    flightlineEventReviewId,
    geometryQCStatusOptions,
    geometryQCStatusProperties,
} from "../../models/MissionReview"
import { TableColumn, TableVariant } from "../../models/Table"
import { unplannedLineLabel } from "../../utils/FlightlineEventUtils"
import { canSplitReview, reviewTableLabel } from "../../utils/MissionReviewUtils"
import { useLocalStorageStore } from "../../zstore/LocalStorageStore"
import { useMissionReviewStore } from "../../zstore/MissionReviewStore"

const pasteColumns: (keyof FlightlineEventReview)[] = ["qc_comment"]

const getExposureId = (nodeInfo: ReviewNodeInfo, nodeName: number) => {
    return nodeInfo.node_events.find((node) => node.node_original_name === nodeName)?.exposure_num
}

interface FlightlineEventReviewTableProps {
    updateRows: (rows: FlightlineEventReview[]) => void
    handleOpenDrawerDetails: (rowId: number, selectedTab: ReviewDrawerTab) => void
}

type FlattenedFlightlineEventReview = FlightlineEventReview &
    FlightlineEvent & { length?: number; start_node_name?: number; stop_node_name?: number }

export const FlightlineEventReviewTable = ({
    updateRows,
    handleOpenDrawerDetails,
}: FlightlineEventReviewTableProps) => {
    const selectedIds = useMissionReviewStore((state) => state.selectedEventIds)
    const setSelectedIds = useMissionReviewStore((state) => state.setSelectedEventIds)
    const isNodeBasedMission = useMissionReviewStore((state) => state.isNodeBasedMission)

    const eventReviews = useMissionReviewStore((state) => state.flightLineReviews) ?? []

    const handleBatchChangeReviewStatus = (status: GeometryQCStatus, selectedOnly?: boolean) => {
        const updatedReviews: FlightlineEventReview[] = eventReviews.map((review) => {
            if (selectedOnly && !selectedIds.includes(review.fe_review_id)) {
                return review
            }
            return { ...review, qc_status: status }
        })
        updateRows(updatedReviews)
    }

    const getReviewFromReviewId = (reviewId: number) => {
        return eventReviews.find((review) => review.fe_review_id === reviewId)
    }

    const stickyReviewColumns: TableColumn<FlattenedFlightlineEventReview>[] = useMemo(
        () => [
            {
                id: "flightplan_name_short_label",
                label: "Flight Plan - Name",
                renderCell: (rowId) => {
                    const eventReview = getReviewFromReviewId(rowId)

                    return (
                        <Box display={"flex"} alignItems={"center"}>
                            {eventReview?.flightplan_name_short_label}{" "}
                            {eventReview?.segment_number && (
                                <Chip label={`Seg ${eventReview?.segment_number}`} size="small" sx={{ ml: 2 }} />
                            )}
                            {eventReview?.angle_degrees != null && (
                                <Tooltip title={`Heading: ${Math.round(eventReview.angle_degrees)}°`}>
                                    <Icon sx={{ ml: 1 }}>
                                        <ArrowUpward sx={{ transform: `rotate(${eventReview.angle_degrees}deg)` }} />
                                    </Icon>
                                </Tooltip>
                            )}
                        </Box>
                    )
                },
            },
        ],
        [eventReviews],
    )

    const reviewColumns = useMemo((): TableColumn<FlattenedFlightlineEventReview>[] => {
        const columns: TableColumn<FlattenedFlightlineEventReview>[] = [
            {
                id: "line_original_name",
                label: "Original Name",
                minWidth: 100,
                maxWidth: 167,
                renderCell: (rowId) => {
                    const eventReview = getReviewFromReviewId(rowId)
                    return <Box>{eventReview?.event.line_original_name}</Box>
                },
            },
            {
                id: "coll_event_name",
                label: "Sensor Label",
                minWidth: 200,
                renderCell: (rowId) => {
                    const eventReview = getReviewFromReviewId(rowId)
                    return <Box>{eventReview?.event.coll_event_name}</Box>
                },
            },
            {
                id: "flight_status",
                label: "Operator Status",
                renderCell: (rowId) => {
                    const eventReview = getReviewFromReviewId(rowId)
                    return <Box>{eventReview?.event.flight_status}</Box>
                },
            },
            {
                id: "qc_status",
                label: "Review Status",
                minWidth: 220,
                width: 220,
                variant: TableVariant.select,
                options: geometryQCStatusOptions,
                allowNoSelection: true,
                disableOptions: (row: FlightlineEventReview) => (row.from_split_event ? ["None"] : []),
            },
            { id: "qc_comment", label: "Review Notes", minWidth: 220, variant: TableVariant.input },
        ]

        if (isNodeBasedMission) {
            columns.splice(
                1,
                0,
                {
                    id: "start_node_name",
                    label: "First Node (Exp ID)",
                    renderCell: (rowId: number) => {
                        const review = getReviewFromReviewId(rowId)
                        const expID =
                            review?.node_info && getExposureId(review.node_info, review.node_info.start_node_name)
                        return (
                            <Box>
                                {review?.node_info?.start_node_name} {expID ? `(${expID})` : ""}
                            </Box>
                        )
                    },
                },
                {
                    id: "stop_node_name",
                    label: "Last Node (Exp ID)",
                    renderCell: (rowId: number) => {
                        const review = getReviewFromReviewId(rowId)
                        const expID =
                            review?.node_info && getExposureId(review.node_info, review.node_info.stop_node_name)
                        return (
                            <Box>
                                {review?.node_info?.stop_node_name} {expID ? ` (${expID})` : ""}
                            </Box>
                        )
                    },
                },
            )
        } else {
            columns.splice(1, 0, {
                id: "length",
                label: "Length (nMi)",
                renderCell: (rowId: number) => {
                    const review = getReviewFromReviewId(rowId)

                    return <Box>{review?.distance_info?.length}</Box>
                },
            })
        }

        return columns
    }, [...eventReviews])

    const tableEvents: FlattenedFlightlineEventReview[] = useMemo(() => {
        return eventReviews.map((review) => ({
            length: review?.distance_info?.length,
            start_node_name: review?.node_info?.start_node_name,
            stop_node_name: review?.node_info?.stop_node_name,
            ...review.event,
            ...review,
        }))
    }, [eventReviews])

    return (
        <NexTable
            label={reviewTableLabel}
            stickyColumns={stickyReviewColumns}
            columns={reviewColumns}
            primaryKey={flightlineEventReviewId}
            rows={tableEvents}
            updateTableRows={updateRows}
            footerText={`(${unplannedLineLabel}) Unplanned Line`}
            maxHeight={600}
            selectedRowOverlay={(row: FlightlineEventReview) => (
                <Box display={"flex"} gap={2}>
                    <Tooltip title={"Operator Details"}>
                        <IconButton
                            onClick={() => {
                                handleOpenDrawerDetails(row.fe_review_id, ReviewDrawerTab.DETAILS)
                            }}
                        >
                            <Visibility />
                        </IconButton>
                    </Tooltip>
                    {canSplitReview(row) && (
                        <Tooltip title={"Split Line"}>
                            <IconButton
                                onClick={() => {
                                    handleOpenDrawerDetails(row.fe_review_id, ReviewDrawerTab.SPLIT)
                                }}
                            >
                                <CallSplit />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            )}
            canSelect={true}
            pasteColumns={pasteColumns}
            selectedRows={{ selectedIds, setSelectedIds }}
            toolbarContents={
                <>
                    <ChangeEventReviewStatusButton
                        disabled={selectedIds.length === 0}
                        handleUpdate={(status: GeometryQCStatus) => {
                            handleBatchChangeReviewStatus(status, true)
                        }}
                    />
                    <ButtonGroup>
                        <Button
                            sx={{ whiteSpace: "nowrap" }}
                            onClick={() => handleBatchChangeReviewStatus(GeometryQCStatus.ACCEPTED)}
                        >
                            Accept All
                        </Button>
                        <Button
                            sx={{ whiteSpace: "nowrap" }}
                            onClick={() => handleBatchChangeReviewStatus(GeometryQCStatus.REJECTED)}
                        >
                            Reject All
                        </Button>
                    </ButtonGroup>
                </>
            }
        />
    )
}

const ChangeEventReviewStatusButton = ({
    disabled,
    handleUpdate,
}: { disabled: boolean; handleUpdate: (status: GeometryQCStatus) => void }) => {
    const [open, setOpen] = useState(false)
    const anchorRef = useRef<HTMLDivElement>(null)
    const currentStatusAction =
        useLocalStorageStore((state) => state.missionReviewEventStatusAction) ?? GeometryQCStatus.ACCEPTED
    const setCurrentStatusAction = useLocalStorageStore((state) => state.setMissionReviewEventStatusAction)

    const handleClick = () => {
        handleUpdate(currentStatusAction)
    }

    const handleMenuItemClick = (status: GeometryQCStatus) => {
        setCurrentStatusAction(status)
        setOpen(false)
    }

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen)
    }

    const handleClose = (event: Event) => {
        if (anchorRef.current?.contains(event.target as HTMLElement)) {
            return
        }

        setOpen(false)
    }

    const menuId = "status select menu"

    return (
        <>
            <ButtonGroup variant="outlined" ref={anchorRef} aria-label="Change statuses of events button">
                <Button onClick={handleClick} disabled={disabled} sx={{ whiteSpace: "nowrap" }}>
                    {geometryQCStatusProperties(currentStatusAction).actionText}
                </Button>
                <Button
                    size="small"
                    aria-controls={open ? menuId : undefined}
                    aria-expanded={open ? "true" : undefined}
                    aria-label="change event statuses"
                    aria-haspopup="menu"
                    onClick={handleToggle}
                >
                    <ArrowDropDown />
                </Button>
            </ButtonGroup>
            <Popper
                sx={{ zIndex: 1000 }}
                open={open}
                anchorEl={anchorRef.current}
                transition={true}
                disablePortal={true}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin: placement === "bottom" ? "center top" : "center bottom",
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id={menuId} autoFocusItem={true}>
                                    {geometryQCStatusOptions.map((option) => (
                                        <MenuItem
                                            key={option}
                                            selected={option === currentStatusAction}
                                            onClick={() => handleMenuItemClick(option)}
                                        >
                                            Switch to: {geometryQCStatusProperties(option).actionText}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    )
}
